/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSelector } from 'reselect';

import localStorage from '@ibm-watson/shared/modules/ia/util/localStorage';
import type { Account } from '../../../../ts/account';
import type { Assistant } from '../../../../ts/assistant';
import type { InstanceState } from '../../../../ts/instance';
import type { PlanName } from '../../../../ts/plan';
import { SkillType } from '../../../../ts/skill';
import type {
  RootState,
  SessionSlice,
  SessionUser
} from '../../../../ts/state';
import * as crnUtil from '../../util/crn';
import { HAS_SEEN_DIALOG_NOTIFICATION } from '../../util/task-list/constants';
import {
  selectAssistant,
  selectSkillReferencesFromAssistant
} from '../assistants/core-selectors';
import { selectIsNewIa } from '../instance/selectors';

export const isAuthenticated = (state: RootState): boolean =>
  Boolean(state.session?.user);

export const getAccountId = (state: RootState): string | undefined =>
  state.session.account?.account_id;

export const getAccount = (state: RootState): Account | undefined =>
  state.session?.account;

export const selectInstanceId = (state: RootState): string | undefined =>
  state.session?.instance;

export const selectInstanceRegion = (state: RootState): string | undefined =>
  state.session?.region;

export const selectIamId = (state: RootState): string =>
  state.session?.user?.iamId;

/**
 * Selector to get the current service instance id
 * @param {Object} state The current state
 * @return {String} the UUID representing the service instance
 */
export const selectServiceInstanceId = createSelector(
  [selectInstanceId],
  (instance: string): string | null => {
    if (instance && crnUtil.isCrn(instance)) {
      return crnUtil.parse(instance).service_instance;
    }
    return null;
  }
);

export const selectInstanceStatePlan = (
  state: RootState
): PlanName | undefined => state.session?.instanceState?.plan;

export const getTenant = (
  state: RootState
): { region?: string; instance?: string } => ({
  region: state?.session?.region,
  instance: state?.session?.instance
});

/**
 * Factory method that creates a memoized selector which returns the ID of
 * the first skill reference of the specified type associated with the
 * assistant specified in the session (when executing in New IA).
 * When executed in Classic IA, it simply returns the skill ID in the session.
 * When executed in New IA, it will return null if the assistant in the
 * session does not have a skill of the specified type.
 *
 * @param {string} type The skill type to return (either "action" or "dialog")
 * @return {Function} The memorized selector for the specified type
 */
const getSkillIdByTypeFactory = (type: SkillType) =>
  createSelector(
    [
      selectIsNewIa,
      selectAssistant,
      (state: RootState) => state.session?.skillId
    ],
    (
      isNewIa: boolean,
      assistant: Assistant,
      skillId: string
    ): null | string => {
      //FIXME: We can probably remove this function once our code is in typescript
      if (isNewIa) {
        if (assistant) {
          const targetSkill = selectSkillReferencesFromAssistant(
            assistant
          )?.find(skill_reference => skill_reference.type === type);

          return targetSkill?.skill_id ?? null;
        }
      }

      return skillId;
    }
  );

/**
 * Returns the ID of the first action skill associated with the assistant
 * specified in the session (when executing in New IA).
 * When executed in Classic IA, it simply returns the skill ID in the session.
 * When executed in New IA, it will return null if the assistant in the
 * session does not have an action skill.
 *
 * @returns {string} The action skill ID for the active assistant.
 */
export const getActionSkillId = getSkillIdByTypeFactory(SkillType.ACTION);

/**
 * Returns the ID of the first dialog skill associated with the assistant
 * specified in the session (when executing in New IA).
 * When executed in Classic IA, it simply returns the skill ID in the session.
 * When executed in New IA, it will return null if the assistant in the
 * session does not have a dialog skill.
 *
 * @returns {string} The dialog skill ID for the active assistant.
 */
export const getDialogSkillId = getSkillIdByTypeFactory(SkillType.DIALOG);

export const getAssistantId = (state: RootState): string | undefined =>
  state.session?.assistantId;

export const getWorkspaceId = (state: RootState): string | undefined =>
  state.session.workspaceDetail.workspace_id;

export const getWorkspaceDetail = (state: RootState) =>
  state.session.workspaceDetail;

export const getHasSeenDialogNotification = (state: RootState): boolean => {
  const assistantId = state.session?.assistantId;
  const assistantStorage = localStorage.get(assistantId);
  return (
    assistantStorage &&
    HAS_SEEN_DIALOG_NOTIFICATION in assistantStorage &&
    assistantStorage['hasSeenDialogNotification']
  );
};

/**
 * Selector to see if the session is currently being updated asynchronously
 * @param {Object} state The current state
 * @returns {boolean} true if the session state is being updated, otherwise false
 */
export const isChecking = (state: RootState): boolean =>
  !!state.session.checking;

/**
 * Selector to see if the session is has been initialized by an action
 * @param {Object} state The current state
 * @returns {boolean} true if the session state has been initialized, otherwise false
 */
export const isInitialized = (state: RootState): boolean =>
  !!state.session.initialized;

/**
 * Selector to get the active user object from the store
 * @param {Object} state The current state
 * @return {Object} the current user
 */
export const getUser = (state: RootState): SessionUser | undefined =>
  state.session.user;

/**
 * Selector to get the active user email from the store
 * @param {Object} state The current state
 * @return {string} the user email, will be an empty string if it fails to get one.
 */
export const getUserEmail = (state: RootState): string =>
  state.session.user?.email || '';

/**
 * Selector to get the session from the store
 * @param {Object} state The current state
 * @return {Object} the session
 */
export const getSession = (state: RootState): SessionSlice => state.session;

export const selectInstanceState = (
  state: RootState
): InstanceState | undefined => state.session.instanceState;

/**
 * Selector to determine if we are a US based account.
 * Care about Billing Region AND running in US DC.
 *
 * @param {Object} state
 * @returns {boolean}
 */
export const selectInUSA = (state: RootState): boolean => {
  return (
    state.session?.account?.billing_country_code === 'USA' &&
    state.session?.region?.startsWith('us-')
  );
};

export const selectAccountAuthorDomainName = (state: RootState): string => {
  const email = state.session.account?.owner_userid;
  if (email) {
    return email.substring(email.lastIndexOf('@') + 1);
  }
  return '';
};

export const selectRecentEditorDomainName = (state: RootState): string => {
  const email = state.session.user?.email;
  if (email) {
    return email.substring(email.lastIndexOf('@') + 1);
  }
  return '';
};

/**
 * Selector to the user's authentication session has become inactive
 * @param {Object} state The current state
 * @return {boolean} true if the user's authentication session has become inactive, otherwise false
 */
export const selectSessionInactive = (state: RootState): boolean =>
  getSession(state).sessionInactive;

/**
 * Selector to the user's authentication session has been revoked
 * @param {Object} state The current state
 * @return {boolean} true if the user's authentication session has been revoked, otherwise false
 */
export const selectSessionRevoked = (state: RootState): boolean =>
  getSession(state).sessionRevoked;

/**
 * Selector to the user's IP address being restricted and not allowed to authenticate
 * @param {Object} state The current state
 * @return {boolean} true if the user's IP address being restricted and not allowed to authenticate, otherwise false
 */
export const selectIpAddressRestriction = (state: RootState): boolean =>
  getSession(state).ipAddressRestriction;

/**
 * Selector that decides whether the current authentication session is invalid for any reason (i.e. whether the
 * user needs to log out or fix some other permanent issue), which means that all outgoing network calls should
 * be aborted.
 * @param {Object} state The current state
 * @return {boolean} true if the authentication session is no longer valid for any reason.
 */
export const selectSessionInvalid = (state: RootState): boolean =>
  selectSessionInactive(state) ||
  selectSessionRevoked(state) ||
  selectIpAddressRestriction(state);

/**
 * Selector that returns the CSRF token the server returned for the current session.
 * This token will be passed back to the server as a custom header to prevent
 * CSRF attacks.
 *
 * @param {Object} state The current state
 * @return {String} the CSRF token the server returned for the current session
 */

export const selectCsrfToken = (state: RootState): string =>
  getSession(state).csrfToken;
